import React from 'react';
import styled from '@emotion/styled';
import { Button, IconButton, Tooltip } from '@mui/material';
import PolarisIcon from './PolarisIcon';
import useScrollPosition from '@/hooks/useScrollPosition';
import useContainerSize from '@/hooks/useContainerSize';

interface ScrollToTopButtonProps {
  type?: 'text' | 'icon';
  position?: 'left' | 'right' | 'center';
  label?: string;
  size?: 'small' | 'large';
  containerRef?: React.RefObject<HTMLElement>;
}

interface StyledButtonProps {
  visible: boolean;
  size: 'small' | 'large';
  position: 'left' | 'center' | 'right';
  containerRect?: DOMRect | null;
  buttonType: 'icon' | 'text';
}

const ScrollToTopButton: React.FC<ScrollToTopButtonProps> = ({
  type = 'icon',
  position = 'center',
  label = 'Scroll to top',
  size = 'large',
  containerRef
}) => {
  const scrollY = useScrollPosition(containerRef);
  const { containerRect, isLongContainer } = useContainerSize(containerRef);

  const viewportHeight = containerRef?.current?.clientHeight || window.innerHeight;
  const visible = isLongContainer && scrollY > viewportHeight * 0.35;

  const scrollToTop = () => {
    const scrollEl = containerRef?.current || window;

    if (scrollEl instanceof Window) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } else {
      scrollEl.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  return type === 'text' ? (
    <StyledTextBottomButton
      onClick={scrollToTop}
      visible={visible}
      size={size}
      position={position}
      containerRect={containerRect}
      buttonType="text"
      variant="contained"
      color="primary"
    >
      {label}
    </StyledTextBottomButton>
  ) : (
    <Tooltip title={label} disableInteractive>
      <StyledIconButton
        onClick={scrollToTop}
        visible={visible}
        size={size}
        position={position}
        containerRect={containerRect}
        buttonType="icon"
        aria-label={label}
      >
        <PolarisIcon icon="up_double" width={size === 'small' ? 24 : 40} height={size === 'small' ? 24 : 40} />
      </StyledIconButton>
    </Tooltip>
  );
};

export default ScrollToTopButton;

const computePositionStyles = (containerRect: DOMRect | null | undefined, position: 'left' | 'center' | 'right', buttonType: 'text' | 'icon') => {
  const offset = 24;

  if (containerRect) {
    const top = containerRect.bottom;
    const left =
      position === 'center' ? `${containerRect.left + containerRect.width / 2}px` : position === 'left' ? `${containerRect.left + offset}px` : 'auto';
    const right = position === 'right' ? `${offset}px` : 'auto';

    return {
      top: buttonType === 'icon' ? `${top - 12}px` : `${top}px`,
      bottom: 'auto',
      left,
      right,
      transform: `translateY(-100%) ${position === 'center' ? 'translateX(-50%)' : ''}`.trim()
    };
  } else {
    return {
      top: 'auto',
      bottom: buttonType === 'icon' ? '50px' : '0',
      left: position === 'center' ? '50%' : position === 'left' ? `${offset}px` : 'auto',
      right: position === 'right' ? `${offset}px` : 'auto',
      transform: `${position === 'center' ? 'translateX(-50%)' : ''}`.trim()
    };
  }
};

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'visible' && prop !== 'size' && prop !== 'position' && prop !== 'containerRect' && prop !== 'buttonType'
})<StyledButtonProps>`
  position: fixed;
  color: ${({ theme }) => theme.palette.primary.main};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  transition:
    opacity 0.4s ease-in-out,
    transform 0.4s ease-in-out;
  z-index: 2;

  ${({ containerRect, position, buttonType, visible }) => {
    const { top, bottom, left, right, transform } = computePositionStyles(containerRect, position, buttonType);

    const animTransform = visible ? 'translateY(0)' : 'translateY(20px)';

    return `
      top: ${top};
      bottom: ${bottom};
      left: ${left};
      right: ${right};
      transform: ${[transform, animTransform].join(' ')};
    `;
  }}
`;

const StyledTextBottomButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'visible' && prop !== 'position' && prop !== 'containerRect' && prop !== 'buttonType'
})<StyledButtonProps>`
  position: fixed;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  transition:
    opacity 0.4s ease-in-out,
    transform 0.4s ease-in-out;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  white-space: nowrap;
  z-index: 1000;

  ${({ containerRect, position, buttonType }) => {
    const { top, bottom, left, right, transform } = computePositionStyles(containerRect, position, buttonType);

    return `
      top: ${top};
      bottom: ${bottom};
      left: ${left};
      right: ${right};
      transform: ${transform};
    `;
  }}
`;
